import type { Item } from '@hugo/core'
import {
  ArchiveXIcon,
  CheckIcon,
  ClockIcon,
  InfoIcon,
  ListTodoIcon,
  type LucideIcon,
  PanelRightOpenIcon,
  RotateCcwIcon,
  SparklesIcon,
  Trash2Icon,
  UserRoundCheckIcon,
  XIcon,
} from 'lucide-react'
import { useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Kbd } from '@/components/ui/kbd'
import { choiceLabel } from '@/lib/format'
import { useItemActions } from '@/lib/item-actions'
import type { RowMode } from './item-row'
import { SnoozeMenu } from './snooze-menu'
import { useWorkspace } from './workspace'

function Action({
  label,
  shortcut,
  icon: Icon,
  onClick,
  disabled,
}: {
  label: string
  shortcut: string
  icon: LucideIcon
  onClick: () => void
  disabled: boolean
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      aria-label={`${label} (${shortcut})`}
    >
      <Icon aria-hidden="true" />
      {label}
      <Kbd className="max-md:hidden">{shortcut}</Kbd>
    </Button>
  )
}

/**
 * Publishes how much of the bottom of the window the toolbar covers as `--toolbar-inset`, which
 * the page's scroll padding uses so a row scrolled into view never ends up behind the toolbar.
 */
function useToolbarInset() {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const root = document.documentElement
    const update = () => {
      // Fixed at the bottom on phones (bottom: 0), sticky above it on desktop (bottom: 1rem).
      const offset = Number.parseFloat(getComputedStyle(el).bottom) || 0
      root.style.setProperty('--toolbar-inset', `${Math.ceil(el.offsetHeight + offset)}px`)
    }
    update()
    // A resize writes the variable in the next frame, not inside the observer's callback: the
    // toasts are placed with it, and layout changes during ResizeObserver delivery make WebKit
    // report "ResizeObserver loop completed with undelivered notifications".
    let frame = 0
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(update)
    })
    observer.observe(el)
    return () => {
      observer.disconnect()
      cancelAnimationFrame(frame)
      root.style.removeProperty('--toolbar-inset')
    }
  }, [])
  return ref
}

/**
 * Actions for the selected item. Same actions as the keyboard shortcuts; on phones it sits at
 * the bottom of the screen (thumb reach), on desktop below the list. With a suggestion from hugo,
 * Accept and Dismiss come first: in the same row on desktop, in a row of their own above the
 * four-column grid on phones.
 */
export function ItemToolbar({
  mode,
  item,
  timeZone,
}: {
  mode: RowMode
  item: Item | null
  timeZone: string
}) {
  const actions = useItemActions()
  const { openDetails, snoozeOpen, setSnoozeOpen } = useWorkspace()
  const ref = useToolbarInset()
  const off = item === null
  const on = (fn: (it: Item) => unknown) => () => {
    if (item) void fn(item)
  }
  const suggestion = mode === 'inbox' ? (item?.suggestion ?? null) : null

  return (
    <div
      ref={ref}
      role="toolbar"
      aria-label="Actions for the selected item"
      className="fixed inset-x-0 bottom-0 z-20 border-t bg-background/95 px-3 pt-2 pb-[calc(var(--safe-bottom)+0.5rem)] backdrop-blur md:sticky md:bottom-4 md:flex md:flex-wrap md:items-center md:gap-1.5 md:rounded-xl md:border md:shadow-sm"
    >
      {suggestion ? (
        <div
          className="mb-1.5 grid grid-cols-[minmax(0,1fr)_auto] gap-1.5 md:contents"
          data-testid="suggestion-actions"
        >
          <Action
            label={`Accept: ${choiceLabel(suggestion.choice)}`}
            shortcut="A"
            icon={SparklesIcon}
            disabled={off}
            onClick={on(actions.accept)}
          />
          <Action
            label="Dismiss"
            shortcut="D"
            icon={XIcon}
            disabled={off}
            onClick={on(actions.dismissSuggestion)}
          />
          <span aria-hidden="true" className="mx-0.5 hidden h-5 w-px bg-border md:block" />
        </div>
      ) : null}
      <div className="grid grid-cols-4 gap-1.5 md:contents">
        {mode === 'inbox' ? (
          <>
            <Action
              label="Task"
              shortcut="T"
              icon={ListTodoIcon}
              disabled={off}
              onClick={on((it) => actions.triage(it, 'task'))}
            />
            <Action
              label="I owe"
              shortcut="O"
              icon={UserRoundCheckIcon}
              disabled={off}
              onClick={on((it) => actions.triage(it, 'owe'))}
            />
            <Action
              label="Waiting"
              shortcut="W"
              icon={ClockIcon}
              disabled={off}
              onClick={on((it) => actions.triage(it, 'waiting'))}
            />
            <Action
              label="FYI"
              shortcut="F"
              icon={InfoIcon}
              disabled={off}
              onClick={on((it) => actions.triage(it, 'fyi'))}
            />
            <Action
              label="Ignore"
              shortcut="X"
              icon={ArchiveXIcon}
              disabled={off}
              onClick={on((it) => actions.triage(it, 'ignore'))}
            />
          </>
        ) : null}
        {mode === 'list' || mode === 'snoozed' || mode === 'inbox' ? (
          <Action
            label="Done"
            shortcut="E"
            icon={CheckIcon}
            disabled={off}
            onClick={on(actions.complete)}
          />
        ) : null}
        {mode === 'list' || mode === 'snoozed' || mode === 'inbox' ? (
          <SnoozeMenu
            item={item}
            open={snoozeOpen && !off}
            onOpenChange={setSnoozeOpen}
            timeZone={timeZone}
          />
        ) : null}
        {mode === 'list' ? (
          <>
            <Action
              label="Task"
              shortcut="T"
              icon={ListTodoIcon}
              disabled={off || item?.kind === 'task'}
              onClick={on((it) => actions.move(it, 'task'))}
            />
            <Action
              label="I owe"
              shortcut="O"
              icon={UserRoundCheckIcon}
              disabled={off || item?.kind === 'owe'}
              onClick={on((it) => actions.move(it, 'owe'))}
            />
            <Action
              label="Waiting"
              shortcut="W"
              icon={ClockIcon}
              disabled={off || item?.kind === 'waiting'}
              onClick={on((it) => actions.move(it, 'waiting'))}
            />
          </>
        ) : null}
        {mode === 'list' || mode === 'snoozed' ? (
          <Action
            label="Drop"
            shortcut="X"
            icon={Trash2Icon}
            disabled={off}
            onClick={on(actions.drop)}
          />
        ) : null}
        {mode === 'done' ? (
          <Action
            label="Reopen"
            shortcut="R"
            icon={RotateCcwIcon}
            disabled={off}
            onClick={on(actions.reopen)}
          />
        ) : null}
        <Action
          label="Details"
          shortcut="↵"
          icon={PanelRightOpenIcon}
          disabled={off}
          onClick={on((it) => openDetails(it.id))}
        />
      </div>
    </div>
  )
}
